/**
 * UI-side task types.
 *
 * The domain shapes are owned by the backend plugin and imported from
 * `@lognai/plugin-tasks`; only the wire aliases (the backend's `Date` fields
 * arrive as ISO strings over JSON) and the display maps live here.
 */
import type {
  Task as TaskEntity,
  TaskComment as TaskCommentEntity,
  TaskActivity as TaskActivityEntity,
  CreateTaskDto as CreateTaskDtoEntity,
  UpdateTaskDto as UpdateTaskDtoEntity,
  TaskPriority,
  TaskStatus,
} from '@lognai/plugin-tasks';

export type {
  TaskPriority,
  TaskStatus,
  TaskActivityType,
  AssignableUser,
  TaskEntityRef,
} from '@lognai/plugin-tasks';

/** Distributes over `Date | undefined`, so optional dates stay optional. */
type IsoDate<T> = T extends Date ? string : T;
type WireDates<T> = { [K in keyof T]: IsoDate<T[K]> };

export type TaskComment = WireDates<TaskCommentEntity>;
export type TaskActivity = WireDates<TaskActivityEntity>;

export type Task = Omit<WireDates<TaskEntity>, 'comments' | 'activity'> & {
  comments: TaskComment[];
  activity: TaskActivity[];
};

/** An empty string clears `assigneeUserId`/`dueDate`; `undefined` leaves them unchanged. */
export type CreateTaskDto = WireDates<CreateTaskDtoEntity>;
export type UpdateTaskDto = WireDates<UpdateTaskDtoEntity>;

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
};

/**
 * The backend's `TASK_PRIORITIES`/`TASK_STATUSES` cannot be imported as values:
 * `@lognai/plugin-tasks` resolves to a CJS server bundle (core-platform, mongodb),
 * which the browser build cannot take. Deriving them from the exhaustive
 * `Record<TaskPriority|TaskStatus, string>` maps above still makes a new backend
 * priority/status a compile error here.
 */
export const TASK_PRIORITIES = Object.keys(PRIORITY_LABELS) as TaskPriority[];
export const TASK_STATUSES = Object.keys(STATUS_LABELS) as TaskStatus[];

/** Counts behind the Task Board KPI strip. Server-counted; the board is paged. */
export interface TaskSummary {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  mine: number;
  overdue: number;
  dueSoon: number;
  unassigned: number;
  /**
   * Every label in use across the board, open and closed tasks alike.
   *
   * Server-computed for the same reason the counts are: the board is paged, so
   * a label list built from the page in front of you offers only the labels
   * that happen to be on it (QA ledger TASK2-002).
   */
  labels: string[];
}
